import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form';

import AvatarUploader from '@/components/AvatarUploader';
import OnboardingField from '@/domain/onboarding/components/OnboardingField';
import { type OnboardingFormValues } from '@/domain/onboarding/types';
import { PARTNER_CATEGORIES, partnerCategoryLabel } from '@/domain/onboarding/utils';
import { useTranslate } from '@/state/i18n';
import { format } from '@/utils/data';

interface Props {
  control: Control<OnboardingFormValues>;
  setValue: UseFormSetValue<OnboardingFormValues>;
  /** Approved account (Conta, P15): the document and the legal name show but do not change. */
  lockDocument?: boolean;
}

/** Step 1 (design P04 · 1): who promotes the events, as users will see it. */
const OrganizationStep = ({ control, setValue, lockDocument = false }: Props) => {
  const t = useTranslate('domain.onboarding.components.OrganizationStep');
  const [logoUrl, displayName, description] = useWatch({
    control,
    name: ['logoUrl', 'displayName', 'description'],
  });

  return (
    <Stack gap={2.5}>
      <Box sx={sx.logoRow}>
        <AvatarUploader
          imageUrl={logoUrl || null}
          size={72}
          alt={displayName || t('logoAlt')}
          cropModalTitle={t('logoCropTitle')}
          cropShape="rect"
          uploadFolder="partners"
          onImageChange={(url) => setValue('logoUrl', url, { shouldDirty: true })}
          onRemove={() => setValue('logoUrl', '', { shouldDirty: true })}
        />
        <Stack gap={0.5}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t('logo')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('logoHint')}
          </Typography>
        </Stack>
      </Box>
      <Box sx={sx.grid}>
        <OnboardingField
          control={control}
          name="displayName"
          label={t('displayName')}
          hint={t('displayNameHint')}
          placeholder={t('displayNamePlaceholder')}
        />
        <OnboardingField
          control={control}
          name="legalName"
          label={t('legalName')}
          placeholder={t('legalNamePlaceholder')}
          disabled={lockDocument}
          hint={lockDocument ? t('lockedHint') : undefined}
        />
        <OnboardingField
          control={control}
          name="taxId"
          label={t('taxId')}
          placeholder="00.000.000/0000-00"
          inputMode="numeric"
          formatOnBlur={(value) => format.taxId(value)}
          disabled={lockDocument}
          hint={lockDocument ? t('lockedHint') : undefined}
        />
        <OnboardingField control={control} name="category" label={t('category')} select>
          {PARTNER_CATEGORIES.map((category) => (
            <MenuItem key={category} value={category}>
              {partnerCategoryLabel[category]}
            </MenuItem>
          ))}
        </OnboardingField>
      </Box>
      <OnboardingField
        control={control}
        name="description"
        label={t('description')}
        optionalLabel={t('optional')}
        hint={t('descriptionCounter', { count: description.length })}
        placeholder={t('descriptionPlaceholder')}
        multiline
        minRows={3}
        slotProps={{ htmlInput: { maxLength: 280 } }}
      />
    </Stack>
  );
};

export default OrganizationStep;

const sx = {
  logoRow: { display: 'flex', alignItems: 'center', gap: 2 },
  grid: {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
    gap: 2,
  },
};
